import {
  Checkbox,
  Button,
  Input,
  Text,
  Icon,
  Switch,
  Heading,
  IconButton,
  HStack,
  VStack,
} from '@genial/components';
import { Config, Constant } from '@genial/config';
import { BoxBorder, Color, Spacing, Typography } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigation } from '@react-navigation/native';
import { Box, Pressable } from 'native-base';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ActivityIndicator, Linking } from 'react-native';
import * as zod from 'zod';

import { SignedOutNavigationScreenNavigationProp } from '../../../../routes/types';
import { useSessionActions } from '../../../../store/session/actions';
import { TokenNumberInput } from '../TokenNumberInput';

const validationSchema = zod
  .object({
    email: zod.union([
      zod
        .string({ required_error: 'O campo e-mail ou CPF é obrigatório' })
        .min(1, 'O campo e-mail ou CPF é obrigatório')
        .email('Email ou CPF inválido'),
      zod.string().refine(Utils.Validate.validateCpf, 'CPF inválido'),
    ]),
    password: zod
      .string({ required_error: 'O campo senha é obrigatório' })
      .min(1, 'O campo senha é obrigatório'),
    hasToken: zod.boolean(),
    authStep: zod.string().optional(),
    token: zod.string().optional(),
  })
  .refine(
    (data) =>
      !data.hasToken ||
      (data.authStep === 'data' && data.hasToken) ||
      (data.authStep === 'token' && data.hasToken && data.token?.toString().length === 6),
    {
      path: ['token'],
      message: 'Token inválido',
    }
  );

type SignInFormData = zod.infer<typeof validationSchema>;

export const FormSignIn: React.FC<{ isMobile?: boolean }> = ({ isMobile }) => {
  const navigation = useNavigation<SignedOutNavigationScreenNavigationProp>();
  const [isSigning, setIsSigning] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [rememberPassword, setRememberPassword] = useState(false);
  const [authWithToken, setAuthWithToken] = useState(false);
  const [authStep, setAuthStep] = useState<'data' | 'token'>('data');

  const { signIn } = useSessionActions();
  const {
    control,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
  } = useForm<SignInFormData>({
    resolver: zodResolver(validationSchema),
    mode: 'all',
  });

  const handleSignIn = useCallback(
    (data: SignInFormData) => {
      setIsSigning(true);
      signIn(data.email, data.password, data.token).finally(() => {
        setIsSigning(false);
      });
    },
    [signIn]
  );

  const handleSignInOrToken = useCallback(
    (data: SignInFormData) => {
      if (authWithToken && authStep === 'data') {
        setAuthStep('token');
      } else {
        handleSignIn(data);
      }
    },
    [authStep, authWithToken, handleSignIn]
  );

  const handleNewAdvisor = useCallback(async () => {
    const urlIsOpenable = await Linking.canOpenURL(Config.GenialSignupUrl);

    if (urlIsOpenable) {
      await Linking.openURL(Config.GenialSignupUrl);
    }
  }, []);

  const handleHelp = useCallback(async () => {
    const isUrlOpenable = await Linking.canOpenURL(Config.GenialHelpUrl);

    if (isUrlOpenable) {
      await Linking.openURL(Config.GenialHelpUrl);
    }
  }, []);

  const handleForgotPassword = useCallback(() => {
    const email = getValues().email;

    navigation.navigate('ForgotPasswordRequest', {
      emailOrDocument: Utils.Validate.validateCpf(email) ? '' : email,
    });
  }, [getValues, navigation]);

  const handleToggleRememberPassword = useCallback(
    (_selectedValue?: string, selected?: boolean) => {
      if (selected === undefined) {
        setRememberPassword((prevState) => !prevState);
      } else {
        setRememberPassword(selected);
      }
    },
    []
  );

  const handleToggleToken = useCallback((_selectedValue?: string, selected?: boolean) => {
    if (selected === undefined) {
      setAuthWithToken((prevState) => !prevState);
    } else {
      setAuthWithToken(selected);
    }
  }, []);

  const handleBackToData = useCallback(() => {
    setAuthStep('data');
  }, []);

  const handleSetToken = useCallback(
    (token: string) => {
      setValue('token', token);
    },
    [setValue]
  );

  const submitForm = handleSubmit(handleSignInOrToken);
  const { email } = getValues();

  const dataForm = useMemo(
    () => (
      <>
        <Heading testId="singin-title">
          <Text
            fontSize={{ base: Typography['fontsize.lg'], lg: Typography['fontsize.display'] }}
            fontWeight={Typography['fontweight.semibold']}
            color="neutral.black"
            fontFamily={Typography['fontfamily.base.semibold']}
            variant="semibold">
            Acesse sua conta
          </Text>
        </Heading>
        <VStack w="full" space={Spacing['stack.nano']}>
          <Text variant="semibold" color="neutral.black" fontSize={Typography['fontsize.md']}>
            E-mail ou CPF
          </Text>
          <Input
            control={control}
            placeholder="Digite seu e-mail ou CPF"
            keyboardType="email-address"
            name="email"
            autoComplete="email"
            variant="filled"
            hasShadow={false}
            error={errors.email}
            isDisabled={isSigning}
            onSubmitEditing={submitForm}
          />
          <Text
            variant="semibold"
            color="neutral.black"
            fontSize={Typography['fontsize.md']}
            pt={2}>
            Senha
          </Text>
          <Input
            control={control}
            placeholder="Digite sua senha"
            secureTextEntry={!isPasswordVisible}
            name="password"
            autoComplete="password"
            hasShadow={false}
            variant="filled"
            error={errors.password}
            isDisabled={isSigning}
            onSubmitEditing={submitForm}
            inputRightElement={
              <Pressable
                onPress={() => {
                  setIsPasswordVisible(!isPasswordVisible);
                }}
                pr={2}
                testID="button-clear-input">
                <Icon name={isPasswordVisible ? 'Visibility' : 'VisibilityOff'} size="xxs" />
              </Pressable>
            }
          />
        </VStack>
        <HStack
          justifyContent="space-between"
          space={Spacing['inline.xxxs']}
          pt={Spacing['stack.xxxs']}>
          {isMobile ? (
            <Box flexDir="row" justifyContent="center">
              <Switch isChecked={rememberPassword} onToggle={handleToggleRememberPassword} />
              <Text pl={2}>Lembrar meu acesso</Text>
            </Box>
          ) : (
            <Checkbox
              testId="lembre-senha-login"
              onChange={handleToggleRememberPassword}
              options={[
                {
                  label: 'Lembrar meu acesso',
                  value: '1',
                  fontWeight: 'semibold',
                },
              ]}
              selectedValues={rememberPassword ? ['1'] : []}
            />
          )}
          <Pressable onPress={handleForgotPassword}>
            <Text
              textDecorationColor={Color['brand.tertiary.principal']}
              textDecorationLine="underline"
              color="brand.tertiary.principal">
              Esqueci minha senha
            </Text>
          </Pressable>
        </HStack>

        <HStack>
          {isMobile ? (
            <>
              <Switch isChecked={authWithToken} onToggle={handleToggleToken} />
              <Text pl={2}>Autenticar com o Token</Text>
            </>
          ) : (
            <Checkbox
              testId="autenticar-com-token"
              onChange={handleToggleToken}
              options={[
                {
                  label: 'Autenticar com o Token',
                  value: '1',
                  fontWeight: 'semibold',
                },
              ]}
              selectedValues={authWithToken ? ['1'] : []}
            />
          )}
        </HStack>
      </>
    ),
    [
      authWithToken,
      control,
      errors.email,
      errors.password,
      handleForgotPassword,
      handleToggleRememberPassword,
      handleToggleToken,
      isMobile,
      isPasswordVisible,
      isSigning,
      rememberPassword,
      submitForm,
    ]
  );

  const tokenForm = useMemo(
    () => (
      <>
        <Heading testId="singin-title">
          <Text
            fontSize={{ base: Typography['fontsize.lg'], lg: Typography['fontsize.display'] }}
            fontWeight={Typography['fontweight.semibold']}
            color="neutral.black"
            fontFamily={Typography['fontfamily.base.semibold']}
            variant="light">
            Validação de acesso
          </Text>
        </Heading>
        <VStack space={Spacing['stack.nano']}>
          <Text
            fontSize={{ base: Typography['fontsize.md'], lg: Typography['fontsize.lg'] }}
            fontWeight={Typography['fontweight.semibold']}>
            Precisamos validar o token para sua segurança
          </Text>
          <Text fontSize={{ base: Typography['fontsize.md'] }} color="neutral.dark">
            No app google authenticator, procure por “genial investimentos” e informe os números
            apresentados.
          </Text>
          <Box paddingY={Spacing['inset.nano']}>
            <TokenNumberInput onChangeText={handleSetToken} isDisabled={isSigning} />
            {errors?.token?.message && (
              <Text
                testID="token-message-error"
                fontSize={Typography['fontsize.xs']}
                lineHeight="distant"
                color="feedback.error.default">
                {errors?.token?.message}
              </Text>
            )}
          </Box>
          <Text
            fontSize={{ base: Typography['fontsize.md'], lg: Typography['fontsize.lg'] }}
            fontWeight={Typography['fontweight.semibold']}>
            Ainda não tem o token habilitado?
          </Text>
          <Text fontSize={{ base: Typography['fontsize.md'] }} color="neutral.dark">
            Entre no portal e clique no ícone do seu usuário, localizado no canto superior direito.
            Em seguida, selecione a opção “Ver Perfil” e depois clique em “Habilitar Token”.
          </Text>
        </VStack>
      </>
    ),
    [errors.token, handleSetToken]
  );

  const loginActions = useMemo(() => {
    if (authStep === 'data') {
      return (
        <>
          <Button
            rightIcon={
              isSigning ? (
                <ActivityIndicator
                  style={{ alignSelf: 'flex-end' }}
                  color={Color['neutral.white']}
                  size="small"
                />
              ) : undefined
            }
            isDisabled={isSigning}
            onPress={submitForm}>
            Entrar
          </Button>
          <HStack
            justifyContent="center"
            space={Spacing['stack.xxxs']}
            p={{ md: Spacing['stack.xxxs'] }}>
            <Text fontSize={Typography['fontsize.md']}>Ainda não é um parceiro Genial?</Text>
            <Pressable onPress={handleNewAdvisor}>
              <Text
                fontSize={Typography['fontsize.md']}
                textDecorationColor={Color['brand.tertiary.principal']}
                textDecorationLine="underline"
                color="brand.tertiary.principal">
                Cadastre-se
              </Text>
            </Pressable>
          </HStack>
          {isMobile && (
            <Pressable onPress={handleHelp}>
              <HStack justifyContent="center" alignItems="center" space={Spacing['stack.nano']}>
                <Text
                  variant="semibold"
                  fontSize={Typography['fontsize.md']}
                  color="brand.tertiary.principal">
                  PRECISA DE AJUDA?
                </Text>
                <Icon name="Headphone" color={Color['brand.tertiary.principal']} size="xs" />
              </HStack>
            </Pressable>
          )}
        </>
      );
    }

    return (
      <Box flexDirection="row" justifyContent="space-between">
        <IconButton
          testId="auth-step-back-button"
          icon="ArrowLeft"
          onPress={handleBackToData}
          iconColor={Color['brand.tertiary.principal']}
        />
        <Button
          rightIcon={
            isSigning ? (
              <ActivityIndicator
                style={{ alignSelf: 'flex-end' }}
                color={Color['neutral.white']}
                size="small"
              />
            ) : (
              <Icon name="ArrowRight" size="sm" color={Color['neutral.white']} />
            )
          }
          onPress={submitForm}>
          Entrar
        </Button>
      </Box>
    );
  }, [authStep, handleBackToData, handleHelp, handleNewAdvisor, isMobile, isSigning, submitForm]);

  useEffect(() => {
    const loadRememberPassword = async () => {
      const remembering =
        (await Utils.Storage.GetItem(Constant.SessionRememberToggleKey)) === 'true';
      const storedLogin = await Utils.Storage.GetItem(Constant.SessionLoginKey);

      setRememberPassword(remembering);

      if (remembering && storedLogin !== null) {
        setValue('email', storedLogin);
      }
    };

    loadRememberPassword();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setValue('hasToken', authWithToken);
    setValue('authStep', authStep);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authWithToken, authStep]);

  useEffect(() => {
    Utils.Storage.SetItem(Constant.SessionRememberToggleKey, String(rememberPassword));

    if (rememberPassword && (Utils.Validate.validateCpf(email) || !errors.email?.message)) {
      Utils.Storage.SetItem(Constant.SessionLoginKey, email);
    }
  }, [email, errors.email?.message, rememberPassword]);

  return (
    <Box
      flex={1}
      justifyContent="center"
      alignItems={{ lg: 'flex-end' }}
      paddingX={{ lg: Spacing['stack.md'] }}>
      <VStack
        p={Spacing['stack.sm']}
        width={{ base: 'full', lg: '1/3', xl: '1/4' }}
        minHeight={{ lg: '5/6' }}
        bgColor={Color['neutral.white']}
        borderRadius={BoxBorder.heavy}
        space={Spacing['stack.xxs']}
        justifyContent="center">
        {authStep === 'data' ? dataForm : tokenForm}
        {loginActions}
      </VStack>
    </Box>
  );
};
